import { useSetAtom } from 'jotai';
import { ReactElement, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { IconButton } from '@creditornot/cb-button';
import { legacyToolsTypographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { navigationModalOpenAtom } from 'common/state/navigationState';

import { AppEnvWarning } from './ProductionWarning';
import { WarehouseSelector } from './WarehouseSelector';
import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { useShowStaticMenu } from '../../../hooks/useShowStaticMenu';
import { media } from '../../../media';
import { DevIndicator } from '../DevIndicator';
import { Stack } from '../Stack';

const Root = styled.header<{ $hideBottomBorder: boolean }>`
  position: sticky;
  top: 0;
  right: 0;
  left: 0;
  z-index: 2;
  flex-shrink: 0;
  background-color: ${color.bg};
  color: ${color.text};
  ${({ $hideBottomBorder }) =>
    !$hideBottomBorder &&
    css`
      border-bottom: 1px solid ${color.border};
    `};

  padding-left: 16px;
  padding-right: 16px;
  @media ${media.sm} {
    padding-left: 24px;
    padding-right: 24px;
  }

  overflow: hidden;
`;

const TopContentStack = styled(Stack)`
  height: 64px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const BottomContentContainer = styled.div<{ $disableBottomContentPadding: boolean }>`
  margin-bottom: ${({ $disableBottomContentPadding }) => ($disableBottomContentPadding ? 0 : 8)}px;
`;

const LeftContentStack = styled(Stack)`
  min-width: 0;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-right: 12px;
  position: relative;
`;

const TitleSection = styled.div`
  overflow: hidden;
`;

const TitleContainer = styled.h1<{ $hasTitleContent: boolean }>`
  ${legacyToolsTypographyCss.Title1()}
  margin: 0;
  white-space: nowrap;
  font-weight: 700;
  color: ${color.text};
  min-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media ${media.md} {
    ${({ $hasTitleContent }) => {
      if ($hasTitleContent) {
        return legacyToolsTypographyCss.Heading6();
      }
      return legacyToolsTypographyCss.Heading5();
    }}
  }
`;

const RightContentStack = styled(Stack)`
  position: relative;
  display: flex;
  flex: 0 0 auto;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 16px;
`;

const StyledAppEnvWarning = styled(AppEnvWarning)`
  margin: 0 -16px;

  @media ${media.sm} {
    margin: 0 -24px;
  }
`;

const StyledDevIndicator = styled(DevIndicator)`
  position: absolute;
  padding: 1px 4px;
  top: 4px;
  left: 28px;
`;

const RedDot = styled.span`
  background-color: ${color.textNegative};
  position: absolute;
  top: 16px;
  left: 28px;
  border-radius: 50%;
  height: 14px;
  width: 14px;

  border: 2px solid ${color.bg};
`;

type Props = {
  children?: ReactNode;
  titleContent?: ReactNode;
  rightContent?: ReactNode;
  title: ReactNode;
  disableContentPadding?: boolean;
  disableBottomContentPadding?: boolean;
  className?: string;
  bottomContent?: ReactNode;
  showWarehouseSelector?: boolean;
  hideBottomBorder?: boolean;
  hideMenuIcon?: boolean;
  onBackClick?: () => void;
  displayNotificationDot?: boolean;
};

export const NavigationBar = ({
  className,
  disableBottomContentPadding = false,
  titleContent,
  rightContent,
  title,
  bottomContent,
  showWarehouseSelector = false,
  hideBottomBorder = false,
  hideMenuIcon = false,
  onBackClick,
  displayNotificationDot = false,
}: Props): ReactElement => {
  const f = useFormatMessage();
  const setShowModal = useSetAtom(navigationModalOpenAtom);
  const showStaticMenu = useShowStaticMenu();

  const showMenuIcon = !showStaticMenu && !onBackClick && !hideMenuIcon;

  const buttons = (
    <>
      {onBackClick && (
        <IconButton
          size="small"
          variant="neutral"
          onClick={onBackClick}
          data-test-id="navbar-back-button"
          aria-label={f('components.navigation_bar.go_back')}
          icon={icons.arrowLeft}
        />
      )}
      {showMenuIcon && (
        <IconButton
          data-test-id="navbar-menu-button"
          aria-label={f('components.navigation_bar.navigation_menu')}
          onClick={() => setShowModal(true)}
          variant="neutral"
          size="small"
          icon={icons.threeLines}
        />
      )}
    </>
  );

  return (
    <Root className={className} $hideBottomBorder={hideBottomBorder} id="app-header">
      <StyledAppEnvWarning />
      <TopContentStack direction="row" justifyContent="space-between">
        <LeftContentStack direction="row" alignItems="center" gap="regularXxlarge">
          {buttons}
          {!showStaticMenu && <StyledDevIndicator size="tiny" />}
          {showMenuIcon && displayNotificationDot && <RedDot data-test-id="notification-dot" />}
          <TitleSection>
            <TitleContainer
              $hasTitleContent={Boolean(titleContent) || showWarehouseSelector}
              data-test-id="navbar-title"
            >
              {title}
            </TitleContainer>
            <Stack direction="row" gap="regularXxlarge">
              {titleContent}
              {showWarehouseSelector && <WarehouseSelector />}
            </Stack>
          </TitleSection>
        </LeftContentStack>
        {rightContent && (
          <RightContentStack direction="row" alignItems="center" className="right-content">
            {rightContent}
          </RightContentStack>
        )}
      </TopContentStack>
      {bottomContent && (
        <BottomContentContainer $disableBottomContentPadding={disableBottomContentPadding}>
          {bottomContent}
        </BottomContentContainer>
      )}
    </Root>
  );
};
